import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, Check, Loader2, Send } from 'lucide-react';
import { PROJECT_TYPES, BUDGET_OPTIONS } from '../data/pricingData';
import { GROWTH_PROJECT_TYPES, MARKETING_INVESTMENT_OPTIONS } from '../data/seoGeoData';
import { AD_BUDGET_OPTIONS, AD_STATUS_OPTIONS, PAID_MEDIA_PROJECT_TYPES } from '../data/paidMediaData';
import { trackEvent } from '../lib/track';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const INITIAL = {
  name: '',
  business: '',
  email: '',
  phone: '',
  website_url: '',
  project_type: '',
  budget: '',
  marketing_investment: '',
  ad_budget: '',
  ad_status: '',
  launch_date: '',
  details: '',
  consent: false,
};

function validate(form) {
  if (!form.name.trim()) return 'Please enter your name.';
  if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
    return 'Please enter a valid email address.';
  }
  if (!form.details.trim()) return 'Please tell us a little about your project.';
  if (!form.consent) return 'Please confirm you are happy for us to contact you about your enquiry.';
  return null;
}

// Turn a backend error into a friendly, human message (esp. Pydantic 422s).
function friendlyApiError(err) {
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail)) {
    const emailErr = detail.find((d) => Array.isArray(d.loc) && d.loc.includes('email'));
    if (emailErr) return 'That email address doesn’t look quite right — please double-check it.';
    const first = detail[0];
    if (first?.loc?.includes('details')) return 'Please tell us a little about your project.';
    if (first?.loc?.includes('name')) return 'Please enter your name.';
    return 'Please check the highlighted details and try again.';
  }
  if (typeof detail === 'string') return detail;
  return 'Something went wrong sending your enquiry — please try again, or email team@boostmarketing.agency.';
}

export default function PricingEnquiry() {
  const [form, setForm] = useState(INITIAL);
  const [hp, setHp] = useState(''); // honeypot spam trap
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const onPrefill = (e) => {
      setSent(false);
      setForm((f) => ({ ...f, ...e.detail }));
    };
    window.addEventListener('boost-prefill-enquiry', onPrefill);
    return () => window.removeEventListener('boost-prefill-enquiry', onPrefill);
  }, []);

  const set = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm({ ...form, [field]: value });
  };

  const emailValid = EMAIL_RE.test(form.email.trim());

  const submit = async (e) => {
    e.preventDefault();
    if (hp) return; // bot filled the hidden field — silently drop
    const err = validate(form);
    if (err) { setError(err); return; }
    setError(null);
    setSending(true);
    try {
      await axios.post(`${API}/website-enquiry`, form);
      trackEvent('enquiry_form_submit', { project_type: form.project_type || 'unspecified' });
      if (GROWTH_PROJECT_TYPES.includes(form.project_type)) {
        trackEvent('website_plus_growth_enquiry', { project_type: form.project_type });
      }
      if (PAID_MEDIA_PROJECT_TYPES.includes(form.project_type)) {
        trackEvent('paid_media_enquiry_submit', { project_type: form.project_type });
      }
      setSent(true);
      setForm(INITIAL);
    } catch (submitErr) {
      console.error('Enquiry submit failed', submitErr);
      setError(friendlyApiError(submitErr));
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="pp-final" id="enquiry" data-testid="pricing-enquiry">
      <div className="pp-container">
        <div className="pp-section-head" style={{ marginBottom: 0 }}>
          <h2 className="pp-h2 pp-display">LET’S BUILD SOMETHING THAT <em style={{ color: '#FF4D94' }}>WORKS.</em></h2>
          <p className="pp-lead">
            Tell us about your business, your current website and what you would like the new website
            to achieve. We will review your requirements and recommend the most suitable route forward.
          </p>
        </div>

        <form className="pp-form" onSubmit={submit} noValidate data-testid="pricing-form">
          {sent ? (
            <div className="pp-form-success" data-testid="pricing-form-success">
              <CheckCircle2 size={46} />
              <h3 className="pp-display">ENQUIRY RECEIVED — THANK YOU!</h3>
              <p>
                Your enquiry is with the Boost! team. We will review your requirements and come back to
                you within one working day with the most suitable next step.
              </p>
              <button
                type="button"
                className="pp-btn pp-btn-outline"
                style={{ marginTop: 20 }}
                onClick={() => setSent(false)}
                data-testid="pricing-form-again"
              >
                Send another enquiry
              </button>
            </div>
          ) : (
            <>
              {/* honeypot — hidden from humans, bots tend to fill it */}
              <div className="pp-hp" aria-hidden="true">
                <label htmlFor="pp-company-website">Company website</label>
                <input id="pp-company-website" type="text" tabIndex="-1" autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} />
              </div>

              <div className="pp-form-row">
                <div className="pp-field">
                  <label htmlFor="pq-name">Name *</label>
                  <input id="pq-name" type="text" placeholder="Your name" value={form.name} onChange={set('name')} data-testid="enquiry-name" />
                </div>
                <div className="pp-field">
                  <label htmlFor="pq-business">Business name</label>
                  <input id="pq-business" type="text" placeholder="Your business" value={form.business} onChange={set('business')} data-testid="enquiry-business" />
                </div>
              </div>
              <div className="pp-form-row">
                <div className="pp-field">
                  <label htmlFor="pq-email">Email address *</label>
                  <div className={`pp-input-wrap ${emailValid ? 'is-valid' : ''}`}>
                    <input id="pq-email" type="email" placeholder="you@business.co.uk" value={form.email} onChange={set('email')} data-testid="enquiry-email" />
                    {emailValid && <Check size={18} strokeWidth={3} className="pp-valid-tick" data-testid="enquiry-email-valid" />}
                  </div>
                </div>
                <div className="pp-field">
                  <label htmlFor="pq-phone">Telephone number</label>
                  <input id="pq-phone" type="tel" placeholder="Optional" value={form.phone} onChange={set('phone')} data-testid="enquiry-phone" />
                </div>
              </div>
              <div className="pp-form-row">
                <div className="pp-field">
                  <label htmlFor="pq-url">Existing website URL</label>
                  <input id="pq-url" type="url" placeholder="https:// (if you have one)" value={form.website_url} onChange={set('website_url')} data-testid="enquiry-url" />
                </div>
                <div className="pp-field">
                  <label htmlFor="pq-type">Type of project</label>
                  <select id="pq-type" value={form.project_type} onChange={set('project_type')} data-testid="enquiry-type">
                    <option value="">Select…</option>
                    {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div className="pp-form-row">
                <div className="pp-field">
                  <label htmlFor="pq-budget">Estimated budget</label>
                  <select id="pq-budget" value={form.budget} onChange={set('budget')} data-testid="enquiry-budget">
                    <option value="">Select…</option>
                    {BUDGET_OPTIONS.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <div className="pp-field">
                  <label htmlFor="pq-invest">Current monthly marketing investment</label>
                  <select id="pq-invest" value={form.marketing_investment} onChange={set('marketing_investment')} data-testid="enquiry-marketing-investment">
                    <option value="">Select… (optional)</option>
                    {MARKETING_INVESTMENT_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              </div>
              <div className="pp-form-row">
                <div className="pp-field">
                  <label htmlFor="pq-adbudget">Approximate monthly advertising budget</label>
                  <select id="pq-adbudget" value={form.ad_budget} onChange={set('ad_budget')} data-testid="enquiry-ad-budget">
                    <option value="">Select… (for paid media)</option>
                    {AD_BUDGET_OPTIONS.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <div className="pp-field">
                  <label htmlFor="pq-adstatus">Current advertising status</label>
                  <select id="pq-adstatus" value={form.ad_status} onChange={set('ad_status')} data-testid="enquiry-ad-status">
                    <option value="">Select… (optional)</option>
                    {AD_STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="pp-field">
                <label htmlFor="pq-launch">Preferred launch date</label>
                <input id="pq-launch" type="text" placeholder="e.g. early September" value={form.launch_date} onChange={set('launch_date')} data-testid="enquiry-launch" />
              </div>
              <div className="pp-field">
                <label htmlFor="pq-details">Project details *</label>
                <textarea id="pq-details" rows={5} placeholder="Tell us about your business, your current website and what you would like the new website to achieve…" value={form.details} onChange={set('details')} data-testid="enquiry-details" />
              </div>
              <label className="pp-consent" htmlFor="pq-consent">
                <input id="pq-consent" type="checkbox" checked={form.consent} onChange={set('consent')} data-testid="enquiry-consent" />
                <span>
                  I am happy for Boost! Design &amp; Marketing to contact me about this enquiry and to
                  store my details in line with its privacy practices. *
                </span>
              </label>
              {error && <p className="pp-form-error" role="alert" data-testid="enquiry-error">{error}</p>}
              <button type="submit" className="pp-btn pp-btn-primary pp-btn-lg" style={{ width: '100%' }} disabled={sending} data-testid="enquiry-submit">
                {sending ? <Loader2 size={18} className="spin" /> : <Send size={17} />}
                {sending ? 'Sending…' : 'Request a website proposal'}
              </button>
            </>
          )}
        </form>
      </div>
    </section>
  );
}
